import './BudgetBuilder.scss';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { OutcomeVariableType } from '@blisspointmedia/bpm-types/dist/Forecasting';
import { Dispatch, ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react';
import { SegmentedControlButton } from '@src/Components/SegmentedControlButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { useSetError } from '../../../redux/modals';
import { Button } from '../../../Components/Button/Button';
import { Appearance, Placement, Size, StretchMode } from '../../../Components/designConstants';
import { Dropdown } from '../../../Components/Dropdown';
import { DropdownOption, MenuItem } from '../../../Components/Dropdown/dropdown-constants';
import { downloadJSONToCSV } from '../../../utils/download-utils';
import {
  BudgetBuilderGroup,
  BudgetOutcome,
  EMPTY_BUDGET_BUILDER_DATA
} from './budgetBuilderContract';
import { buildFullRows, buildSummaryRows } from './budgetBuilderExport';
import { pickSeries, resolveIndex } from './budgetBuilderMath';
import { BudgetCard } from './Components/BudgetCard';
import { BudgetTable } from './Components/BudgetTable';
import {
  BudgetBuilderForecastRef,
  BudgetBuilderQueryData,
  useBudgetBuilderGroupData
} from './useBudgetBuilderGroupData';

const ALL = 'All';
// Sentinel for isolating groups the forecast ran above tactic grain (empty
// `tactic`). Its VALUE is a reserved token, kept distinct from the display
// label, so a real tactic literally tagged "No tactic" can't collide with the
// sentinel. Offered only when tagged and untagged groups coexist.
const NO_TACTIC_VALUE = '__no_tactic__';
const NO_TACTIC_OPTION: MenuItem<string> = { value: NO_TACTIC_VALUE, label: 'No tactic' };

type ViewMode = 'plots' | 'table';
const VIEW_OPTIONS: { key: ViewMode; label: string }[] = [
  { key: 'plots', label: 'Plots' },
  { key: 'table', label: 'Table' }
];

const EMPTY_DATA: BudgetBuilderQueryData = {
  ...EMPTY_BUDGET_BUILDER_DATA,
  allNotFound: false,
  partiallyFailedForecastIds: []
};

const distinct = (values: string[]): string[] => [...new Set(values.filter(Boolean))];

// Resets to ALL when the current value drops out of validOptions; ALL itself is always valid.
const useResettingFilter = (
  validOptions: string[]
): readonly [string, Dispatch<SetStateAction<string>>] => {
  const [value, setValue] = useState(ALL);
  useEffect(() => {
    if (value !== ALL && !validOptions.includes(value)) {
      setValue(ALL);
    }
  }, [validOptions, value]);
  return [value, setValue] as const;
};

interface BbFilterProps {
  label: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
}

const BbFilter = ({ label, value, options, onChange }: BbFilterProps): ReactElement => (
  <div className='bb-filter'>
    <Dropdown
      overlayPlacement={Placement.RIGHT}
      size={Size.LG}
      label={label}
      value={value}
      options={options}
      onChange={onChange}
    />
  </div>
);

interface BudgetBuilderProps {
  /** Page-level Outcome Variable (from the Forecasting subheader) drives which series each card shows. */
  outcomeVar?: OutcomeVariableType;
  /** Forecast date range (BpmDateStrings), shown as a reminder that figures apply to this period. */
  period?: { start: string; end: string };
  /** Every forecast in the current group. */
  forecasts?: BudgetBuilderForecastRef[];
  /** Company whose S3 folder holds the forecasts' files; differs from cid on a test company. */
  dataCompany?: string;
  /** True until the page's own forecast-groups fetch resolves (see the isPending note below). */
  forecastGroupsLoading: boolean;
  /** Every saved forecast group, for the period banner's group switcher — single-entry renders plain text instead. */
  groupOptions: MenuItem<string>[];
  selectedGroupId?: string;
  onSelectGroup: (groupId: string) => void;
}

const formatPeriod = (period?: { start: string; end: string }): string | null => {
  if (!period?.start || !period?.end) {
    return null;
  }
  return `${format(parseISO(period.start), 'MMM d, yyyy')} – ${format(parseISO(period.end), 'MMM d, yyyy')}`;
};

/** Number of weeks in the forecast period — converts weekly curve figures to period totals. */
const periodWeeks = (period?: { start: string; end: string }): number | undefined => {
  if (!period?.start || !period?.end) {
    return undefined;
  }
  const days = differenceInCalendarDays(parseISO(period.end), parseISO(period.start)) + 1;
  return days > 0 ? days / 7 : undefined;
};

export const BudgetBuilder = ({
  outcomeVar,
  period,
  forecasts,
  dataCompany,
  forecastGroupsLoading,
  groupOptions,
  selectedGroupId,
  onSelectGroup
}: BudgetBuilderProps): ReactElement => {
  // isPending, not isLoading — isLoading is isPending && isFetching, so it reads
  // false while the query is disabled (e.g. cid hasn't hydrated yet), which would
  // let the empty message flash before there's actually been a chance to fetch.
  // Also OR in forecastGroupsLoading: with no forecasts the S3 query is
  // permanently disabled, so isPending alone can't tell "still waiting on
  // forecast groups" apart from "resolved to zero forecasts."
  const { data, isPending, error } = useBudgetBuilderGroupData(forecasts, dataCompany);
  const isLoading = forecastGroupsLoading || (isPending && Boolean(forecasts?.length));
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load Budget Builder data",
        message: `Failed to load budget builder data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <BudgetBuilderBody
      data={data ?? EMPTY_DATA}
      isLoading={isLoading}
      outcomeVar={outcomeVar}
      period={period}
      groupOptions={groupOptions}
      selectedGroupId={selectedGroupId}
      onSelectGroup={onSelectGroup}
    />
  );
};

interface BudgetBuilderBodyProps {
  data: BudgetBuilderQueryData;
  /** Data hasn't resolved yet — shows a loading state in place of the cards, without hiding the toolbar/filters. */
  isLoading: boolean;
  outcomeVar?: OutcomeVariableType;
  period?: { start: string; end: string };
  groupOptions: MenuItem<string>[];
  selectedGroupId?: string;
  onSelectGroup: (groupId: string) => void;
}

const BudgetBuilderBody = ({
  data,
  isLoading,
  outcomeVar,
  period,
  groupOptions,
  selectedGroupId,
  onSelectGroup
}: BudgetBuilderBodyProps): ReactElement => {
  const [view, setView] = useState<ViewMode>('plots');
  // Per-group selected point index, owned here so the plots and the table show
  // the same chosen budget. Sparse: a missing entry means "use the default".
  const [selection, setSelection] = useState<Record<string, number>>({});
  const selectedOutcome: BudgetOutcome = outcomeVar === 'Revenue' ? 'revenue' : 'volume';

  const channelOptions = useMemo(() => distinct(data.groups.map(g => g.channel)), [data.groups]);
  const kpiOptions = useMemo(() => distinct(data.groups.map(g => g.kpi)), [data.groups]);
  // Tactic isn't available at every grain — only offer the filter when some
  // group actually carries a tactic.
  const tacticValues = useMemo(() => distinct(data.groups.map(g => g.tactic)), [data.groups]);
  // Whether any group ran above tactic grain — gates the "No tactic" option so
  // it only appears when there's actually a mix of tagged and untagged groups.
  const hasUntaggedTactic = useMemo(() => data.groups.some(g => !g.tactic), [data.groups]);
  // Empty (not just missing NO_TACTIC_VALUE) when no group carries a tactic at
  // all, so a stale "No tactic" selection still resets once the filter itself
  // stops being offered.
  const tacticValidOptions = useMemo(
    () =>
      tacticValues.length > 0
        ? [...tacticValues, ...(hasUntaggedTactic ? [NO_TACTIC_VALUE] : [])]
        : [],
    [tacticValues, hasUntaggedTactic]
  );

  const [channelFilter, setChannelFilter] = useResettingFilter(channelOptions);
  const [kpiFilter, setKpiFilter] = useResettingFilter(kpiOptions);
  const [tacticFilter, setTacticFilter] = useResettingFilter(tacticValidOptions);

  const filtered = useMemo(
    () =>
      data.groups.filter(
        g =>
          // Drop groups with no curve series — the forecast can omit an outcome
          // at coarser grains; without this the page/table/export would crash.
          g.series.length > 0 &&
          (channelFilter === ALL || g.channel === channelFilter) &&
          (tacticFilter === ALL ||
            (tacticFilter === NO_TACTIC_VALUE ? !g.tactic : g.tactic === tacticFilter)) &&
          (kpiFilter === ALL || g.kpi === kpiFilter)
      ),
    [data.groups, channelFilter, tacticFilter, kpiFilter]
  );

  const setGroupIndex = (groupId: string, index: number): void =>
    setSelection(prev => ({ ...prev, [groupId]: index }));

  const downloadSummary = (): void =>
    downloadJSONToCSV(buildSummaryRows(filtered, selectedOutcome, selection), 'budget-builder');
  const downloadFull = (): void =>
    downloadJSONToCSV(buildFullRows(filtered, selectedOutcome), 'budget-builder-full');

  const effectivePeriod = period ?? data.period;
  const periodLabel = formatPeriod(effectivePeriod);
  const weeks = periodWeeks(effectivePeriod);

  return (
    <div className='budget-builder-container' data-testid='budget-builder'>
      <div className='bb-toolbar'>
        <SegmentedControlButton
          size={Size.LG}
          options={VIEW_OPTIONS}
          selectedOption={view}
          onChange={setView}
        />
        <div className='bb-filter-bar'>
          <BbFilter
            label='Channel'
            value={channelFilter}
            options={[ALL, ...channelOptions]}
            onChange={setChannelFilter}
          />
          <BbFilter
            label='KPI'
            value={kpiFilter}
            options={[ALL, ...kpiOptions]}
            onChange={setKpiFilter}
          />
          {tacticValues.length > 0 && (
            <BbFilter
              label='Tactic'
              value={tacticFilter}
              options={[ALL, ...tacticValues, ...(hasUntaggedTactic ? [NO_TACTIC_OPTION] : [])]}
              onChange={setTacticFilter}
            />
          )}
        </div>
        <div className='bb-period'>
          <span className='bb-period-label'>Forecast period</span>
          {groupOptions.length > 1 ? (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.SM}
              stretch={StretchMode.FIT}
              value={selectedGroupId}
              placeholder='Select a period'
              options={groupOptions}
              onChange={onSelectGroup}
            />
          ) : (
            <span className='bb-period-value'>{periodLabel || '--'}</span>
          )}
        </div>
      </div>

      <p className='bb-intro'>
        All figures are weekly, for the forecast period. Channels are independent from one another
        (i.e. adjustments to one won&rsquo;t affect another).
        {view === 'plots' && (
          <>
            {' '}
            Drag a point on a curve, or move any slider — spend, outcome, and efficiency stay in
            sync.
          </>
        )}
      </p>

      {!isLoading && data.partiallyFailedForecastIds.length > 0 && (
        <div className='bb-partial-warning'>
          {conditionallyPluralizeWithCount(data.partiallyFailedForecastIds.length, 'forecast')} in
          this group failed to load — showing partial results.
        </div>
      )}

      <BudgetBuilderContent
        isLoading={isLoading}
        filtered={filtered}
        allNotFound={data.allNotFound}
        view={view}
        selectedOutcome={selectedOutcome}
        weeks={weeks}
        selection={selection}
        setGroupIndex={setGroupIndex}
        downloadSummary={downloadSummary}
        downloadFull={downloadFull}
      />
    </div>
  );
};

interface BudgetBuilderContentProps {
  isLoading: boolean;
  filtered: BudgetBuilderGroup[];
  allNotFound: boolean;
  view: ViewMode;
  selectedOutcome: BudgetOutcome;
  weeks?: number;
  selection: Record<string, number>;
  setGroupIndex: (groupId: string, index: number) => void;
  downloadSummary: () => void;
  downloadFull: () => void;
}

const BudgetBuilderContent = ({
  isLoading,
  filtered,
  allNotFound,
  view,
  selectedOutcome,
  weeks,
  selection,
  setGroupIndex,
  downloadSummary,
  downloadFull
}: BudgetBuilderContentProps): ReactElement => {
  if (isLoading) {
    return (
      <div className='bb-loading'>
        <Spinner />
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className='bb-empty'>
        {allNotFound
          ? 'Forecasts in this group are still processing — check back soon.'
          : 'No spend groups match the current filters.'}
      </div>
    );
  }

  if (view === 'table') {
    return (
      <div className='bb-table-view'>
        <div className='bb-table-actions'>
          <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={downloadSummary}>
            Download CSV
          </Button>
          <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={downloadFull}>
            Download full data (CSV)
          </Button>
        </div>
        <BudgetTable groups={filtered} selectedOutcome={selectedOutcome} selection={selection} />
      </div>
    );
  }

  return (
    <div className='bb-card-grid'>
      {filtered.map(group => {
        const series = pickSeries(group, selectedOutcome);
        return (
          <BudgetCard
            key={group.id}
            group={group}
            selectedOutcome={selectedOutcome}
            weeks={weeks}
            activeIndex={resolveIndex(selection, group.id, series.curve.length)}
            onActiveIndexChange={i => setGroupIndex(group.id, i)}
          />
        );
      })}
    </div>
  );
};
